import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { Select } from '@components/core-ui/form-elements/Select';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { DisclosureSection } from '@components/core-ui/DisclosureSection';
import { useAPI } from '@providers/useAPI';
import { useModalContext } from '@providers/ModalProvider';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AsrProxyAdminService, PromptsService } from 'app/API';
import { adminOrganizationsKeys } from 'app/queries/adminOrganizations/adminOrganizationsKeys';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { createFetchOrganizationModelChoicesQueryFn } from './api/createFetchOrganizationModelChoicesQueryFn';
import { getModelChoiceErrorMessage } from './helpers/getModelChoiceErrorMessage';
import { Content } from './styles/Content';
import { TOrganizationModelChoices } from './types/TOrganizationModelChoices';
import { TProps } from './types/TProps';

export const AdminAIModelChoiceModal = ({ organization }: TProps) => {
  const { closeModal } = useModalContext();
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const modelChoicesKey = adminOrganizationsKeys.modelChoices(organization.id);

  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [bulkModel, setBulkModel] = useState('');
  const [selectedAsrModelDraft, setSelectedAsrModelDraft] = useState<string | null>(null);

  const modelChoicesQuery = useQuery({
    queryKey: modelChoicesKey,
    queryFn: createFetchOrganizationModelChoicesQueryFn(call),
    gcTime: 0,
    staleTime: Infinity,
  });

  const failedSections = modelChoicesQuery.data?.failedSections;

  useEffect(() => {
    if (failedSections?.length) {
      toast.error(`Failed to load ${failedSections.join(', ')}. Reopen the modal to retry.`, {
        id: 'model-choice-load',
      });
    }
  }, [failedSections]);

  const versions = modelChoicesQuery.data?.versions ?? [];
  const allPrompts = modelChoicesQuery.data?.prompts ?? [];
  const promptModels = modelChoicesQuery.data?.promptModels ?? {};
  const asrDefaultModel = modelChoicesQuery.data?.asrDefaultModel ?? '';
  const asrModels = modelChoicesQuery.data?.asrModels ?? [];
  const asrModel = modelChoicesQuery.data?.asrModel ?? '';
  const selectedAsrModel = selectedAsrModelDraft ?? asrModel;

  const updateModelChoices = async (update: (current: TOrganizationModelChoices) => TOrganizationModelChoices) => {
    await queryClient.cancelQueries({ queryKey: modelChoicesKey });
    queryClient.setQueryData<TOrganizationModelChoices>(modelChoicesKey, (current) =>
      current ? update(current) : current,
    );
  };

  const setPromptModels = (slugs: string[], model: string) =>
    updateModelChoices((current) => ({
      ...current,
      promptModels: { ...current.promptModels, ...Object.fromEntries(slugs.map((slug) => [slug, model])) },
    }));

  const updatePromptModel = (slug: string, model: string) =>
    call(
      () =>
        PromptsService.updatePromptModelForOrganization({
          oid: organization.id,
          slug,
          requestBody: {
            model,
          },
        }),
      { dontRedirectOnError: true },
    );

  const promptModelMutation = useMutation({
    mutationFn: ({ slug, model }: { slug: string; model: string }) => updatePromptModel(slug, model),
    onMutate: () => {
      toast.loading('Checking if model is applicable for this feature...', { id: 'model-check' });
    },
    onSuccess: async (_, { slug, model }) => {
      await setPromptModels([slug], model);
      toast.success('Model updated successfully');
    },
    onError: (error) => {
      toast.error(getModelChoiceErrorMessage(error));
    },
    onSettled: () => {
      toast.remove('model-check');
    },
  });

  const bulkPromptModelMutation = useMutation({
    mutationFn: async (model: string) => {
      const successfulSlugs: string[] = [];

      for (const prompt of allPrompts) {
        try {
          await updatePromptModel(prompt.slug, model);
          successfulSlugs.push(prompt.slug);
        } catch (error) {
          toast.error(getModelChoiceErrorMessage(error));
        }
      }

      return successfulSlugs;
    },
    onMutate: (model) => {
      toast.loading(model ? 'Updating all model selections...' : 'Clearing all model selections...', {
        id: 'model-bulk',
      });
    },
    onSuccess: async (successfulSlugs, model) => {
      await setPromptModels(successfulSlugs, model);

      if (successfulSlugs.length === allPrompts.length) {
        toast.success(model ? 'All models were updated' : 'All models were unset');
      } else {
        toast.error(`${allPrompts.length - successfulSlugs.length} model settings failed to update`);
      }
    },
    onSettled: () => {
      toast.remove('model-bulk');
    },
  });

  const asrModelMutation = useMutation({
    mutationFn: async (model: string) => {
      if (model) {
        await call(
          () =>
            AsrProxyAdminService.setOrganizationModel({
              organizationId: organization.id,
              requestBody: { model },
            }),
          { dontRedirectOnError: true },
        );
        return;
      }

      await call(() => AsrProxyAdminService.deleteOrganizationConfig({ organizationId: organization.id }), {
        dontRedirectOnError: true,
      });
    },
    onMutate: (model) => {
      toast.loading(model ? 'Updating ASR model...' : 'Restoring default ASR model...', { id: 'asr-model' });
    },
    onSuccess: async (_, model) => {
      await updateModelChoices((current) => ({ ...current, asrModel: model }));
      toast.success(model ? 'ASR model updated successfully' : 'Default ASR model restored');
    },
    onError: (error) => {
      toast.error(getModelChoiceErrorMessage(error));
    },
    onSettled: () => {
      toast.remove('asr-model');
    },
  });

  const handleBulkUpdate = (model: string) => {
    if (!allPrompts.length) {
      return;
    }

    bulkPromptModelMutation.mutate(model);
  };

  const isLoading =
    modelChoicesQuery.isLoading ||
    promptModelMutation.isPending ||
    bulkPromptModelMutation.isPending ||
    asrModelMutation.isPending;

  const asrModelOptions =
    selectedAsrModel && !asrModels.includes(selectedAsrModel) ? [selectedAsrModel, ...asrModels] : asrModels;

  return (
    <ModalWrapper width="800px">
      {isLoading ? <AbsoluteSpinner overlay={true} /> : null}
      <ModalTitle>Set ASR and LLM models for organization</ModalTitle>
      <Content>
        <section className="mb-6 border-b border-[var(--color-border)] pb-6">
          <h3 className="mb-3 text-base font-semibold text-[var(--color-text)]">ASR model</h3>
          <div className="grid grid-cols-[minmax(0,1fr)_120px] items-end gap-3">
            <Select
              value={selectedAsrModel}
              options={[
                {
                  label: asrDefaultModel ? 'default (' + asrDefaultModel + ')' : 'default',
                  value: '',
                },
                ...asrModelOptions.map((value) => ({ label: value, value })),
              ]}
              onChange={(_, value) => setSelectedAsrModelDraft(value)}
              label="ASR model"
              required
              name="asr-model"
              disabled={isLoading}
            />
            <OutlinedButton
              className="btn--md w-full"
              onClick={() => asrModelMutation.mutate(selectedAsrModel)}
              disabled={isLoading || selectedAsrModel === asrModel}
            >
              Set
            </OutlinedButton>
          </div>
        </section>

        <section>
          <h3 className="mb-3 text-base font-semibold text-[var(--color-text)]">LLM prompt models</h3>
          <div className="mb-5 grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_248px] items-end gap-3">
            <Select
              value={bulkModel}
              options={versions.map((value) => ({ label: value, value }))}
              onChange={(_, value) => setBulkModel(value)}
              label="Model for all features"
              required
              name="bulk-model"
              disabled={isLoading || !versions.length}
            />
            <div className="flex gap-2">
              <OutlinedButton
                className="btn--md flex-1"
                onClick={() => handleBulkUpdate(bulkModel)}
                disabled={isLoading || !bulkModel}
              >
                Set all
              </OutlinedButton>
              <OutlinedButton className="btn--md flex-1" onClick={() => handleBulkUpdate('')} disabled={isLoading}>
                Unset all
              </OutlinedButton>
            </div>
          </div>

          <DisclosureSection
            title="Advanced settings"
            open={advancedOpen}
            setOpenState={setAdvancedOpen}
            loading={isLoading}
            className="advancedSection"
            titleClassName="!bg-[var(--color-surface)]"
          >
            <div className="flex flex-col w-full">
              {allPrompts.map((prompt) => (
                <div key={prompt.slug} className="promptRow">
                  <div className="promptRow__slug">{prompt.slug}</div>
                  <div className="promptRow__select">
                    <Select
                      value={promptModels[prompt.slug] ?? ''}
                      options={[{ label: 'default', value: '' }, ...versions.map((value) => ({ label: value, value }))]}
                      onChange={(_, value) => promptModelMutation.mutate({ slug: prompt.slug, model: value })}
                      label="Model"
                      required
                      name="model"
                    />
                  </div>
                </div>
              ))}
            </div>
          </DisclosureSection>
        </section>

        <div className="buttonGroup">
          <OutlinedButton onClick={closeModal}>Close</OutlinedButton>
        </div>
      </Content>
    </ModalWrapper>
  );
};
